import clsx from 'clsx';
import { Form, Formik } from 'formik';
import React, { useState } from 'react';
import toast from 'react-hot-toast';
import { useQueryClient } from 'react-query';
import { useNavigate } from 'react-router-dom';
import * as Yup from 'yup';

import {
  useCurrentUser,
  useForumPostAdminUpdate,
  useForumPostCreate,
  useForumPostUpdate,
} from '../../../hooks';
import { forum_post_path } from '../../../routes';
import {
  MATCH_EXACT,
  autocompleteEntities,
  createEntity,
} from '../../../utils/backend-api/entities';
import {
  FORUM_POSTS_KEY,
  ForumPost,
  NewForumPost as NewForumPostType,
} from '../../../utils/backend-api/forumPosts';
import { stripHtml } from '../../../utils/formatting';
import { appendSchemeToUrlString, optionalSchemeURLRegex } from '../../../utils/url_validations';
import {
  CheckBoxField,
  MultiValueAutocompleteField,
  TextAreaField,
  TextField,
} from '../../FormFields';
import { EnableProfileModal } from '../../shared';
import AnonymousPostBlock from './AnonymousPostBlock';
import styles from './styles.module.scss';

const validationSchema = () =>
  Yup.object({
    title: Yup.string().required('A title is required'),
    body: Yup.string().required('A post is required'),
    external_url: Yup.string().matches(optionalSchemeURLRegex, 'The url must be valid'),
  });

export const QUICK_TAG_JOBS = 'jobs';
export const QUICK_TAG_FUNDING = 'funding';
export const QUICK_TAG_LAUNCH = 'launch';

export default function NewForumPostForm({
  initialValues,
  onCreateSuccess,
  isManaging,
}: {
  initialValues: NewForumPostType;
  onCreateSuccess: (forumPost: ForumPost) => void;
  isManaging;
}) {
  const { currentUser } = useCurrentUser();
  const firstName = currentUser?.name?.split(' ')[0] || 'You';
  const isEditing = !!initialValues.id;
  const { mutateAsync: updatePostAsync } = useForumPostUpdate();
  const { mutateAsync: updateAdminPostAsync } = useForumPostAdminUpdate();
  const { mutateAsync: createPostAsync } = useForumPostCreate();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const canComment = currentUser?.permitted_to_comment && currentUser?.can_actually_comment;
  const [canInitiallyComment] = useState(canComment);
  const buttonClasses = clsx(styles.submitButton, 'button button-red button-small');
  const buttonClassesAnonymous = clsx(styles.submitButton, 'button button-dark button-small');
  const buttonText = isEditing ? 'Update' : `Post as ${firstName}`;

  initialValues.body = stripHtml(initialValues.body);

  const onQuickTagClick = async (tagName, values, setFieldValue) => {
    const entities = await autocompleteEntities({
      fragment: tagName,
      relationship_sources: ['manual', 'topic', 'google'],
      order_entities_by: ['is_important', 'pdl_verified'],
      match_type: MATCH_EXACT,
    });
    if (entities.length === 0) return; // this should never happen
    if (values.manual_entities.map((entity) => entity.value).includes(entities[0].value)) {
      setFieldValue(
        'manual_entities',
        values.manual_entities.filter((entity) => entity.value !== entities[0].value)
      );
    } else {
      setFieldValue('manual_entities', [...values.manual_entities, entities[0]]);
    }
  };

  const handleSubmit = async (values) => {
    const data: ForumPost = {
      ...values,
      external_url: appendSchemeToUrlString(values.external_url),
      manual_entities: values?.manual_entities.map((entity) => entity.value),
    };

    if (isEditing) {
      const updateAction = isManaging ? updateAdminPostAsync : updatePostAsync;
      return updateAction(data, {
        onSuccess: (data: ForumPost) => {
          toast.success('Post has been updated!');
          queryClient.invalidateQueries(FORUM_POSTS_KEY);
          navigate(forum_post_path(data.id));
          window.scrollTo(0, 0);
        },
        onError: (e) => {
          console.error(e);
          toast.error('Post was not updated, please try again.');
        },
      });
    }

    return createPostAsync(data, {
      onSuccess: (data: ForumPost) => {
        queryClient.invalidateQueries(FORUM_POSTS_KEY);
        onCreateSuccess(data);
      },
      onError: (e) => {
        console.error(e);
        toast.error('Post was not created, please try again.');
      },
    });
  };

  return (
    <Formik
      initialValues={initialValues}
      onSubmit={handleSubmit}
      validationSchema={validationSchema}
      enableReinitialize={true}
    >
      {({ isSubmitting, submitForm, setFieldValue, values }) => (
        <Form className="form forum-post-form">
          <TextField name="title" label="Title" placeholder="Type Title" />
          {!isManaging && (
            <>
              <TextAreaField
                name="body"
                label="Post"
                autoComplete="off"
                placeholder="Type Text"
                minRows={6}
              />
              <TextField name="external_url" placeholder="Type URL (Optional)" label="Link" />
            </>
          )}
          <div className="TextField form-group">
            <label className="input-label" htmlFor={'Tags'}>
              Tags
            </label>
            <div className="tw-w-full">
              <CheckBoxField
                name="job_tag"
                label="Job Posting (#jobs)"
                size="large"
                className={styles.firstCheckboxGroup}
                onClick={() => onQuickTagClick(QUICK_TAG_JOBS, values, setFieldValue)}
              />
              <CheckBoxField
                name="funding_tag"
                label="Funding Announcement (#funding)"
                size="large"
                onClick={() => onQuickTagClick(QUICK_TAG_FUNDING, values, setFieldValue)}
              />
              <CheckBoxField
                name="product_tag"
                label="Product Launch (#launch)"
                size="large"
                onClick={() => onQuickTagClick(QUICK_TAG_LAUNCH, values, setFieldValue)}
              />
              <MultiValueAutocompleteField
                placeholder="Type tags"
                className="forum-post-tag-input"
                name="manual_entities"
                fetchMethod={(value) =>
                  autocompleteEntities({
                    fragment: value,
                    relationship_sources: ['manual', 'topic', 'google'],
                    order_entities_by: ['is_important', 'pdl_verified'],
                  })
                }
                createMethod={(value) =>
                  createEntity({
                    name: value,
                    entity_type: 'other',
                    word_type: 'common',
                  })
                }
              />
            </div>
          </div>

          <div className={styles.formActions}>
            <div className={styles.formActionsDigest}>
              {(!isEditing || isManaging) && (
                <CheckBoxField
                  name="email_digest_enabled"
                  label="Include in daily forum post email digest"
                  size="large"
                  className="mt0"
                />
              )}
            </div>

            {canComment && (
              <div>
                <button
                  type="submit"
                  className={values.is_anonymous ? buttonClassesAnonymous : buttonClasses}
                  disabled={isSubmitting}
                >
                  {values.is_anonymous ? `Post Anonymously` : buttonText}
                </button>
                {!isEditing && (
                  <AnonymousPostBlock isAnonymous={values.is_anonymous} justify="left" />
                )}
              </div>
            )}
            {!canInitiallyComment && (
              <div className={canComment ? 'hidden' : ''}>
                {!currentUser?.permitted_to_comment ? (
                  'Missing name / username, or not permitted to comment'
                ) : (
                  <EnableProfileModal
                    isLoggedIn={currentUser}
                    isSubscriber={!currentUser?.is_free_edition_user}
                    isTriggeredByComment={true}
                    buttonClasses={buttonClasses}
                    commentButtonText={buttonText}
                    isCommentForForum={true}
                    handleSubmitComment={submitForm}
                  />
                )}
              </div>
            )}
          </div>
        </Form>
      )}
    </Formik>
  );
}
